import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { TIME_CONTROL_PRESETS, formatTimeControl } from "@/lib/time-controls";
import { useChallengeSystem } from "@/hooks/useChallengeSystem";
import { useNavigate } from "react-router-dom";
import { Loader2, Trophy, Zap } from "lucide-react";

interface ChallengeDialogProps {
  targetUserId: string;
  targetUsername: string;
  targetElo?: number;
  isFriend: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChallengeDialog({
  targetUserId,
  targetUsername,
  targetElo,
  isFriend,
  open,
  onOpenChange,
}: ChallengeDialogProps) {
  const [selectedTimeControl, setSelectedTimeControl] = useState<string>('5+0');
  const [customMinutes, setCustomMinutes] = useState<string>('');
  const [customIncrement, setCustomIncrement] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [useCustom, setUseCustom] = useState(false);
  
  const { challengePlayer, loading } = useChallengeSystem();
  const navigate = useNavigate();

  const handleSendChallenge = async () => {
    const timeControl = useCustom 
      ? formatTimeControl(parseInt(customMinutes) || 5, parseInt(customIncrement) || 0)
      : selectedTimeControl;

    // Validate custom time control
    if (useCustom) {
      const mins = parseInt(customMinutes) || 0;
      const inc = parseInt(customIncrement) || 0;
      if (mins < 1 || mins > 180) {
        return;
      }
      if (inc < 0 || inc > 60) {
        return;
      }
    }

    const result = await challengePlayer(targetUserId, targetUsername, timeControl, message);
    
    if (result) {
      onOpenChange(false);
      // Challenge sent, user will be notified when accepted
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Challenge {targetUsername}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Player Info */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <div className="font-semibold">{targetUsername}</div>
              {targetElo && (
                <Badge variant="secondary" className="gap-1 mt-1">
                  <Trophy className="w-3 h-3" />
                  {targetElo} ELO
                </Badge>
              )}
            </div>
            {isFriend && (
              <Badge variant="default">Friend</Badge>
            )}
          </div>

          {/* Time Control Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Time Control</Label>
            <div className="grid grid-cols-3 gap-2">
              {TIME_CONTROL_PRESETS.map((tc) => (
                <Button
                  key={tc.value}
                  variant={selectedTimeControl === tc.value && !useCustom ? "default" : "outline"}
                  className="flex flex-col h-auto py-3"
                  onClick={() => {
                    setSelectedTimeControl(tc.value);
                    setUseCustom(false);
                  }}
                >
                  <span className="text-lg mb-1">{tc.icon}</span>
                  <span className="text-xs font-semibold">{tc.label}</span>
                  <span className="text-[10px] text-muted-foreground">{tc.description}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Custom Time Control */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="use-custom"
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="use-custom" className="cursor-pointer">
                Custom Time Control
              </Label>
            </div>
            
            {useCustom && (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label htmlFor="custom-minutes" className="text-xs">
                    Minutes (1-180)
                  </Label>
                  <Input
                    id="custom-minutes"
                    type="number"
                    min="1"
                    max="180"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    placeholder="5"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="custom-increment" className="text-xs">
                    Increment (0-60)
                  </Label>
                  <Input
                    id="custom-increment"
                    type="number"
                    min="0"
                    max="60"
                    value={customIncrement}
                    onChange={(e) => setCustomIncrement(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label htmlFor="challenge-message" className="text-sm">
              Message (optional)
            </Label>
            <Textarea
              id="challenge-message"
              placeholder="Let's play! Good luck!"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={200}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleSendChallenge} 
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Send Challenge
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
